import React from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { 
  FlaskConical, 
  Database, 
  FileCheck, 
  PenTool, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Users,
  Globe,
  Zap,
  BarChart3
} from 'lucide-react';

const LAB_IMAGE = "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/2jmfx93h_scientists-mix-red-and-orange-chemicals-in-a-glass-2025-01-16-13-51-09-utc.jpg";

const services = [
  {
    id: 'clinical-trial',
    icon: FlaskConical,
    title: 'Clinical Trial Management',
    subtitle: 'End-to-End Trial Excellence',
    description: 'Our comprehensive clinical trial management services ensure seamless execution from study initiation to closeout. We leverage our veteran-led discipline and AI-powered analytics to deliver exceptional outcomes.',
    features: [
      { title: 'Study Feasibility & Site Selection', description: 'Strategic site identification based on therapeutic expertise and patient access' },
      { title: 'Patient Recruitment & Retention', description: 'Innovative strategies to accelerate enrollment and minimize dropouts' },
      { title: 'Clinical Monitoring & Risk-Based Management', description: 'Proactive oversight with advanced risk detection systems' },
      { title: 'Vendor & Supply Chain Coordination', description: 'Streamlined logistics ensuring timely delivery' },
      { title: 'Regulatory Compliance & Ethics Support', description: 'Expert guidance on IRB/IEC submissions' },
      { title: 'Real-Time AI-Powered Analytics', description: 'Predictive insights for data-driven decisions' }
    ]
  },
  {
    id: 'biostatistics',
    icon: Database,
    title: 'Biostatistics & Data Management',
    subtitle: 'Precision in Every Data Point',
    description: 'Our biostatistics and data management team combines scientific rigor with advanced technology to ensure data integrity and regulatory compliance throughout your clinical program.',
    features: [
      { title: 'Sclin EDC Platform', description: 'Proprietary Electronic Data Capture with 40% faster build time' },
      { title: 'Database Design & Validation', description: 'Robust systems ensuring 21 CFR Part 11 compliance' },
      { title: 'Real-Time Data Cleaning', description: 'Automated discrepancy management and resolution' },
      { title: 'Statistical Consulting', description: 'Expert guidance on study design and sample size' },
      { title: 'Interim & Final Analysis', description: 'Comprehensive statistical analysis with CDISC outputs' },
      { title: 'AI-Enhanced Modeling', description: 'Advanced analytics for outcome prediction' }
    ]
  },
  {
    id: 'regulatory',
    icon: FileCheck,
    title: 'Regulatory Affairs',
    subtitle: 'Navigating Global Compliance',
    description: 'Our regulatory affairs experts provide strategic guidance and hands-on support to navigate complex global regulatory landscapes, accelerating your path to market approval.',
    features: [
      { title: 'IND, NDA, BLA, ANDA & 510(k)', description: 'Complete submission preparation and management' },
      { title: 'Regulatory Strategy & Gap Analysis', description: 'Strategic planning for optimal development pathways' },
      { title: 'Clinical Trial Applications', description: 'CTA and IND filings across global markets' },
      { title: 'Health Authority Meeting Support', description: 'Preparation for FDA, EMA, MHRA meetings' },
      { title: 'Labeling & Post-Market Compliance', description: 'Regulatory labeling review and compliance' },
      { title: 'Risk Assessment', description: 'Comprehensive regulatory risk evaluation' }
    ]
  },
  {
    id: 'medical-writing',
    icon: PenTool,
    title: 'Medical Writing',
    subtitle: 'Clear, Compliant Documentation',
    description: 'Our medical writing team delivers high-quality clinical and regulatory documents that meet the highest standards of scientific accuracy and regulatory compliance.',
    features: [
      { title: 'Clinical Study Protocols', description: 'Comprehensive protocols with clear scientific rationale' },
      { title: 'Investigator Brochures', description: 'Complete IB development and updates' },
      { title: 'Clinical Study Reports (CSRs)', description: 'ICH-compliant reports for regulatory submission' },
      { title: 'Regulatory Submission Dossiers', description: 'IND, NDA, BLA, CTD, 510(k) documentation' },
      { title: 'Manuscripts & Publications', description: 'Peer-reviewed journal article preparation' },
      { title: 'Lay Summaries', description: 'Patient-focused documents for informed consent' }
    ]
  },
  {
    id: 'pharmacovigilance',
    icon: Shield,
    title: 'Pharmacovigilance & Safety',
    subtitle: 'Protecting Patient Safety',
    description: 'Our pharmacovigilance services ensure comprehensive safety monitoring throughout the product lifecycle, from clinical development through post-marketing surveillance.',
    features: [
      { title: 'AE & SAE Case Processing', description: 'Rapid adverse event intake, processing, and reporting' },
      { title: 'Aggregate Safety Reports', description: 'PSUR, PBRER, and DSUR preparation' },
      { title: 'Risk Management Plans', description: 'RMP and REMS development and implementation' },
      { title: 'Signal Detection', description: 'Proactive safety signal identification' },
      { title: 'Medical Monitoring', description: 'Expert medical review of safety data' },
      { title: 'Post-Marketing Surveillance', description: 'Ongoing safety monitoring in commercial phase' }
    ]
  },
  {
    id: 'drug-recall',
    icon: AlertTriangle,
    title: 'Drug Recall Management',
    subtitle: 'Rapid Response & Compliance',
    description: 'When product safety issues arise, our experienced team provides swift, comprehensive recall management services to protect patients and maintain regulatory compliance.',
    features: [
      { title: 'Regulatory Coordination', description: 'Direct communication with FDA and health authorities' },
      { title: 'Risk Assessment & Strategy', description: 'Evidence-based approach to recall classification' },
      { title: 'Product Retrieval & Traceability', description: 'Comprehensive tracking and retrieval systems' },
      { title: 'Stakeholder Notification', description: 'Clear communications to all stakeholders' },
      { title: 'Post-Recall Compliance', description: 'Documentation and reporting to regulators' },
      { title: 'Corrective Actions', description: 'Root cause analysis and prevention measures' }
    ]
  }
];

export const Services = ({ onDemoClick }) => {
  const location = useLocation();

  React.useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-[#090B17] pt-24" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#8F262B]/5 to-transparent" />
        <div className="w-full px-6 sm:px-12 lg:px-20 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-label">Our Services</span>
              <h1 className="text-4xl sm:text-5xl font-outfit font-bold text-silver-shine mt-4 mb-6" data-testid="services-title">
                Comprehensive CRO <span className="text-gradient">Solutions</span>
              </h1>
              <p className="text-[#C9C9CD] text-lg mb-8">
                From concept to cure, we navigate the complexities of clinical research so you 
                can focus on innovation. Our end-to-end services cover every aspect of clinical 
                development.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[{ icon: Globe, label: 'Global Expertise' }, { icon: Zap, label: 'AI-Powered' }, { icon: Users, label: 'Veteran-Led' }, { icon: BarChart3, label: 'Data-Driven' }].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="icon-box w-10 h-10">
                      <item.icon className="w-5 h-5" />
                    </div>
                    <span className="text-[#C9C9CD]">{item.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden glow-subtle">
                <img src={LAB_IMAGE} alt="Clinical Research Lab" className="w-full" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      {services.map((service, index) => (
        <section 
          key={service.id}
          id={service.id}
          className={`py-20 ${index % 2 === 0 ? 'section-dark' : 'section-surface'} border-y border-[#2E3446] scroll-mt-24`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="w-full px-6 sm:px-12 lg:px-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="icon-box w-16 h-16">
                  <service.icon className="w-8 h-8" />
                </div>
                <div>
                  <span className="text-[#8F262B] text-sm font-medium">{service.subtitle}</span>
                  <h2 className="text-3xl font-outfit font-bold text-silver-shine">{service.title}</h2>
                </div>
              </div>

              <p className="text-[#C9C9CD] text-lg mb-10">
                {service.description}
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                {service.features.map((feature, featureIndex) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: featureIndex * 0.05 }}
                    className="card-gradient p-5"
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#8F262B] flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-silver-shine font-medium mb-1">{feature.title}</h4>
                        <p className="text-[#9F999F] text-sm">{feature.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-20">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-gradient rounded-2xl p-12 text-center"
          >
            <h2 className="section-title mb-4">
              Ready to Partner with <span className="text-gradient">VetForce?</span>
            </h2>
            <p className="text-[#C9C9CD] max-w-xl mx-auto mb-8">
              Let's discuss how our services can accelerate your clinical development program
            </p>
            <button 
              onClick={onDemoClick}
              className="btn-gradient px-8 py-4 inline-flex items-center gap-2"
              data-testid="services-cta-btn"
            >
              Request a Consultation
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
